import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import AuthService from "../services/AuthService";
import { logger } from "@/infrastructure/lib/logger";

export default function OAuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // 1. Extrair tokens do hash fragment (colocados pelo Supabase após OAuth)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const errorParam = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        // 2. Extrair invite_token de MÚLTIPLAS fontes (em ordem de prioridade)
        //    - URL query params (mais confiável SE GoTrue preservar)
        //    - sessionStorage (funciona se tab mantiver storage)
        //    - localStorage (SEMPRE funciona, com expiration check de 5 min)
        const searchParams = new URLSearchParams(window.location.search);
        const inviteTokenFromUrl = searchParams.get('invite_token');
        const inviteTokenFromSession = sessionStorage.getItem('invite_token');

        let inviteTokenFromLocal: string | null = null;
        const localToken = localStorage.getItem('prodgy_pending_invite_token');
        const localTs = localStorage.getItem('prodgy_pending_invite_ts');
        if (localToken && localTs) {
          const elapsed = Date.now() - parseInt(localTs, 10);
          // Aceitar apenas se foi salvo nos últimos 15 minutos
          if (elapsed < 15 * 60 * 1000) {
            inviteTokenFromLocal = localToken;
          }
        }

        const pendingInviteToken = inviteTokenFromUrl || inviteTokenFromSession || inviteTokenFromLocal;

        logger.info('[OAuthCallback] Callback iniciado', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          hasInviteToken: !!pendingInviteToken
        });

        if (errorParam) {
          logger.error('[OAuthCallback] OAuth error', { error: errorParam, description: errorDescription });
          navigate(`/auth/error?message=${encodeURIComponent(errorDescription || errorParam)}`, { replace: true });
          return;
        }

        if (!accessToken) {
          throw new Error("No access token in URL hash");
        }

        if (!refreshToken) {
          throw new Error("No refresh token in URL hash");
        }

        // 3. Sincronizar sessão OAuth com o backend (cria/atualiza profile)
        //    Se pendingInviteToken existir, envia junto para processamento ATÔMICO
        //    (igual ao fluxo de senha: tudo numa única request)
        const authService = AuthService.getInstance();
        const syncResult = await authService.syncOAuthSession(accessToken, refreshToken, pendingInviteToken);
        logger.info('[OAuthCallback] syncOAuthSession resultado', {
          success: syncResult.success,
          needsOnboarding: syncResult.data?.needsOnboarding,
          inviteAccepted: syncResult.data?.inviteAccepted
        });

        if (!syncResult.success) {
          if (syncResult.errorCode === 'PENDING_INVITE') {
            navigate('/auth/error?message=pending_invite', { replace: true });
            return;
          }
          throw new Error(syncResult.message || "Failed to sync OAuth session");
        }

        if (!syncResult.data) {
          throw new Error("Failed to sync OAuth session");
        }

        // 3b. Verificar se houve erro de convite (ex: email mismatch)
        //     Mostra tela de erro amigável ao invés de prosseguir
        if (syncResult.data.inviteErrorCode === 'EMAIL_MISMATCH') {
          logger.warn('[OAuthCallback] Email mismatch detectado');
          // Limpar tokens do storage — convite não foi aceito
          if (pendingInviteToken) {
            sessionStorage.removeItem('invite_token');
            localStorage.removeItem('prodgy_pending_invite_token');
            localStorage.removeItem('prodgy_pending_invite_ts');
          }
          const errorUrl = `/auth/error?message=${encodeURIComponent(syncResult.data.inviteError || 'email mismatch')}` +
            (pendingInviteToken ? `&invite_token=${encodeURIComponent(pendingInviteToken)}` : '');
          window.location.replace(errorUrl);
          return;
        }

        // 4. Armazenar tokens no localStorage
        if (syncResult.data.tokens?.accessToken) {
          localStorage.setItem('access_token', syncResult.data.tokens.accessToken);
        }
        if (syncResult.data.tokens?.refreshToken) {
          localStorage.setItem('refresh_token', syncResult.data.tokens.refreshToken);
        }

        // 5. Limpar invite tokens do storage (consumidos ou não, evitar reuso)
        if (pendingInviteToken) {
          sessionStorage.removeItem('invite_token');
          localStorage.removeItem('prodgy_pending_invite_token');
          localStorage.removeItem('prodgy_pending_invite_ts');
        }

        // 6. Redirecionar baseado no resultado
        //    SEMPRE usar window.location.replace (hard redirect) para forçar o AuthContext
        //    a reinicializar e validar o custom token via validateSession.
        //    navigate() não funciona porque refreshSession falha com custom tokens.
        //    Convite aceito também respeita needsOnboarding: o convidado entra
        //    com onboarding pendente (perfil/survey) — bug 84095.
        if (!syncResult.data.needsOnboarding) {
          logger.info('[OAuthCallback] Redirecionando para /');
          window.location.replace('/');
        } else {
          logger.info('[OAuthCallback] Redirecionando para /onboarding');
          window.location.replace('/onboarding');
        }
      } catch (err) {
        logger.error('[OAuthCallback] Error processing callback', { error: err instanceof Error ? err.message : String(err) });
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <LoadingSpinner className="h-12 w-12" />
    </div>
  );
}
